import { KitType } from '@repo/types';
import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';

import { lowRelativeAbundanceLimit } from 'assets/constants';
import { capitalize } from 'helpers';
import { useCurrentKitAndTinyAccount } from 'hooks/useCurrentKitAndTinyAccount';
import useGutType from 'services/gutType/useGutType';
import { TaxaData } from 'services/microbiome/types/taxa-data.types';
import { getTaxaBarChartDataQuery } from 'services/microbiome/useTaxaBarChartData';
import { getTaxaData } from 'services/microbiome/useTaxaData';

const categoryColors: { [key: string]: string } = {
  beneficial: 'green',
  protective: 'green',
  unfriendly: 'red',
  disruptive: 'red',
  variable: 'orange',
  unknown: 'grey',
};

export interface MicrobiomeBreakdownData {
  gutType: string;
  microbeCount: number;
  bacteriaCategories: Array<{ name: string; color: string; value: number }>;
}

export const useMicrobiomeBreakdownData = (): MicrobiomeBreakdownData => {
  const { gutType } = useGutType();
  const { currentKit, currentKitId, currentKitType } =
    useCurrentKitAndTinyAccount();

  const { data } = useQuery(
    getTaxaBarChartDataQuery(currentKit?.id, currentKit?.sampling_type),
  );

  const { data: taxaData } = useQuery<TaxaData[]>({
    queryKey: [
      'api',
      `${currentKitType ?? KitType.STOOL}InsightsBacteria`,
      currentKitId,
    ],
    queryFn: async () =>
      currentKitId
        ? await getTaxaData(currentKitId, lowRelativeAbundanceLimit)
        : null,
    enabled: !!currentKitId,
  });

  const microbeCount = useMemo(() => {
    if (!taxaData) return 0;
    return taxaData.filter(
      (item: { taxonomy_category?: string }) =>
        item.taxonomy_category === 'species',
    ).length;
  }, [taxaData]);

  const bacteriaCategories = useMemo(() => {
    if (!data) return [];

    const categories: Array<{ name: string; color: string; value: number }> =
      [];

    Object.keys(data).forEach(category => {
      const value = data[category as keyof typeof data];
      const numericValue = Number(value);

      if (!isNaN(numericValue) && numericValue > 0) {
        categories.push({
          name: capitalize(category),
          color: categoryColors[category] || 'grey',
          value: numericValue,
        });
      }
    });

    return categories.sort((a, b) => b.value - a.value);
  }, [data]);

  return {
    gutType,
    microbeCount,
    bacteriaCategories,
  };
};
